import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { validateSignLink, signAgreement } from '../services/api';
import { PublicSplitData, SignAgreementRequest } from '../types';
import { useToast } from './Toast';
import ExpiredLinkComponent from './ExpiredLinkComponent';
import InvalidLinkComponent from './InvalidLinkComponent';
import SigningSuccessComponent from './SigningSuccessComponent';
import AlreadySignedComponent from './AlreadySignedComponent';

type PageState = 'loading' | 'expired' | 'invalid' | 'error' | 'READY_TO_SIGN' | 'ALREADY_SIGNED' | 'completed';

interface SignError {
  type: 'expired' | 'error' | 'network';
  message: string;
  showResendHelp?: boolean;
}

const PublicSignView: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [pageState, setPageState] = useState<PageState>('loading');
  const [signData, setSignData] = useState<PublicSplitData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [signError, setSignError] = useState<SignError | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [typedName, setTypedName] = useState('');
  const [consent, setConsent] = useState(false);
  const { showToast, ToastComponent } = useToast();

  useEffect(() => {
    const checkInviteStatus = async () => {
      if (!token) {
        setPageState('invalid');
        setError('Invalid signing link');
        return;
      }

      const result = await validateSignLink(token);
      
      switch (result.status) {
        case 'expired':
          setPageState('expired');
          setSignData(result.data || null);
          break;
          
        case 'invalid':
          setPageState('invalid');
          setError(result.error || 'This invite link is not valid.');
          break;
          
        case 'valid':
          const status = result.data?.status;
          if (status === 'ALREADY_SIGNED') {
            setPageState('ALREADY_SIGNED');
          } else {
            setPageState('READY_TO_SIGN');
          }
          setSignData(result.data || null);
          break;
          
        case 'error':
        case 'network_error':
          setPageState('error');
          setError(result.error || 'Failed to validate link');
          break;
      }
    };
    
    if (token) {
      checkInviteStatus();
    }
  }, [token]);

  const handleSignSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !signData) return;

    try {
      setIsSubmitting(true);
      setSignError(null);
      
      const signRequest: SignAgreementRequest = {
        token,
        typedName: typedName.trim(),
        consent
      };

      const result = await signAgreement(signRequest);
      
      // Update sign data with the result
      setSignData({
        ...signData,
        signedAt: result.signedAt,
        signedName: result.typedName,
        status: 'ALREADY_SIGNED',
        userAlreadyExists: result.userAlreadyExists
      });
      
      setPageState('completed');
      showToast('Split confirmed successfully!', 'success');
      
    } catch (error: any) {
      if (error.message === 'EXPIRED_DURING_SIGNING') {
        setSignError({
          type: 'expired',
          message: 'This confirmation link expired while you were confirming. Please request a new link.',
          showResendHelp: true
        });
      } else if (error.message.includes('Network')) {
        setSignError({
          type: 'network',
          message: 'Network error. Please check your connection and try again.'
        });
      } else {
        setSignError({
          type: 'error',
          message: 'Failed to record confirmation. Please try again.'
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state
  if (pageState === 'loading') {
    return (
      <div className="min-h-screen bg-[#18181B] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-pink-400"></div>
          <p className="mt-2 text-content-secondary">Validating confirmation link...</p>
        </div>
      </div>
    );
  }

  // Expired state
  if (pageState === 'expired') {
    return (
      <ExpiredLinkComponent 
        splitTitle={signData?.splitTitle}
        collaboratorName={signData?.collaborator?.name}
        ownerName={signData?.ownerName}
        expiresOn={signData?.expiresAt}
      />
    );
  }

  // Invalid or error state
  if (pageState === 'invalid' || pageState === 'error') {
    return (
      <InvalidLinkComponent 
        message={error || 'This invite link is not valid.'}
        showContactSupport={true}
      />
    );
  }

  // Already signed state
  if (pageState === 'ALREADY_SIGNED') {
    return (
      <AlreadySignedComponent 
        signData={signData!}
      />
    );
  }

  // Completed state (just signed)
  if (pageState === 'completed') {
    return (
      <SigningSuccessComponent 
        signData={signData!}
      />
    );
  }

  // Normal signing flow (READY_TO_SIGN)
  if (!signData) {
    return (
      <InvalidLinkComponent 
        message="Unable to load split data"
        showContactSupport={true}
      />
    );
  }

  return (
    <div className="min-h-screen bg-[#18181B] flex items-center justify-center p-4">
      {ToastComponent}
      
      <div className="max-w-2xl w-full bg-[#27272A] border border-[#3F3F46] rounded-2xl shadow-xl p-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="mx-auto w-16 h-16 bg-[#14B8A633] rounded-full flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-[#14B8A6]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">
            Confirm Split
          </h1>
          <p className="text-[#9CA3AF]">
            Please review and confirm your share of the split below
          </p>
        </div>

        {/* Split Details */}
        <div className="bg-[#14B8A610] border border-[#3F3F46] rounded-2xl p-6 mb-8">
          <h2 className="text-xl font-semibold text-white mb-4">"{signData.splitTitle}"</h2>
          
          <div className="grid gap-4">
            <div className="flex justify-between items-center py-2 border-b border-[#3F3F46]">
              <span className="text-[#9CA3AF]">Your Name:</span>
              <span className="font-medium text-white">{signData.collaborator.name}</span>
            </div>
            
            <div className="flex justify-between items-center py-2 border-b border-[#3F3F46]">
              <span className="text-[#9CA3AF]">Your Role:</span>
              <span className="font-medium text-white capitalize">
                {signData.collaborator.role === 'both' ? 'Composer & Author' : signData.collaborator.role}
              </span>
            </div>
            
            <div className="flex justify-between items-center py-2 border-b border-[#3F3F46]">
              <span className="text-[#9CA3AF]">Your Share:</span>
              <span className="font-medium text-white">{signData.collaborator.share}%</span>
            </div>
            
            <div className="flex justify-between items-center py-2">
              <span className="text-[#9CA3AF]">Split Creator:</span>
              <span className="font-medium text-white">{signData.ownerName}</span>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {signError && (
          <div className={`border-l-4 rounded-lg p-4 mb-6 ${
            signError.type === 'expired' 
              ? 'bg-amber-500/10 border-amber-400' 
              : 'bg-red-500/10 border-red-400'
          }`}>
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className={`h-5 w-5 ${
                  signError.type === 'expired' ? 'text-amber-400' : 'text-red-400'
                }`} fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className={signError.type === 'expired' ? 'text-amber-300' : 'text-red-300'}>
                  {signError.message}
                </p>
                {signError.showResendHelp && (
                  <p className={`text-sm mt-1 ${signError.type === 'expired' ? 'text-amber-400' : 'text-red-400'}`}>
                    Please contact {signData.ownerName} to request a new invitation link.
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Signing Form */}
        <form onSubmit={handleSignSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-[#9CA3AF] mb-2">
              Type your full name to confirm this split:
            </label>
            <input
              type="text"
              value={typedName}
              onChange={(e) => setTypedName(e.target.value)}
              className="w-full px-4 py-3 bg-[#3F3F46] border border-[#3F3F46] rounded-xl text-white placeholder-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-[#14B8A6] focus:border-transparent"
              placeholder="Enter your full name"
              required
              disabled={isSubmitting}
            />
          </div>

          <div className="flex items-start">
            <input
              type="checkbox"
              id="consent"
              checked={consent}
              onChange={(e) => setConsent(e.target.checked)}
              className="mt-1 h-4 w-4 text-[#14B8A6] focus:ring-[#14B8A6] border-[#3F3F46] rounded bg-[#3F3F46]"
              required
              disabled={isSubmitting}
            />
            <label htmlFor="consent" className="ml-2 text-sm text-[#9CA3AF]">
              I agree to the terms of this songwriting split and confirm that the information above is accurate.
            </label>
          </div>

          <button
            type="submit"
            disabled={!typedName.trim() || !consent || isSubmitting}
            className="btn-primary w-full py-3"
          >
            {isSubmitting ? 'Confirming...' : 'Confirm Split'}
          </button>
        </form>

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-[#3F3F46] text-center text-sm text-[#9CA3AF]">
          <p>
            Questions? Contact {signData.ownerName} or{' '}
            <a href="mailto:help@cotune.app" className="text-[#14B8A6] hover:text-[#0D9488] underline">
              Cotune Support
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PublicSignView;
